import { getCachedUser } from "./userCache";
import { logger } from "./logger";
import { formatUserDisplayName } from "../lib/utils";

/**
 * Formats a name to show first name and only first letter of last name
 * e.g. "John Doe" becomes "John D."
 * @param name The full name to format
 * @returns The compacted name format
 */
export const formatCompactName = (name: string | undefined | null): string => {
  if (!name) return '';
  
  // First ensure name has proper capitalization
  const formattedName = formatUserDisplayName(name);
  
  // Split into parts
  const parts = formattedName.split(/\s+/);
  
  // If just one name, return it
  if (parts.length <= 1) return formattedName;
  
  // Otherwise, return first name + first letter of last name with period
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1].charAt(0);
  
  return `${firstName} ${lastInitial}.`;
};

/**
 * Gets a formatted assignee name using the user cache first, then falling back to a provided name
 * @param assigneeId The user ID to look up in the cache
 * @param options Configuration options
 * @param options.fallbackName Optional name to use if user not found in cache
 * @param options.compact Whether to use compact format (e.g., "John D.") or full name
 * @returns Formatted name in the requested format
 */
export const getFormattedAssigneeName = (
  assigneeId: string | null | undefined, 
  options: { 
    fallbackName?: string;
    compact?: boolean;
  } = { compact: true }
): string => {
  if (!assigneeId) return 'Assigned';
  
  const { fallbackName, compact = true } = options;
  
  // 1. Use the cached user data (more accurate, fetches directly)
  const cachedUser = getCachedUser(assigneeId);
  if (cachedUser) {
    const name = cachedUser.display_name || cachedUser.email;
    return compact ? formatCompactName(name) : formatUserDisplayName(name);
  }
  
  // 2. Fall back to provided fallback name if available
  if (fallbackName) {
    return compact ? formatCompactName(fallbackName) : formatUserDisplayName(fallbackName);
  }
  
  return 'Assigned';
};

/**
 * Gets the initials of a name (first letter of first name and first letter of last name)
 * @param name The name to get initials from
 * @returns The initials (up to 2 characters)
 */
export const getInitials = (name: string | undefined | null): string => {
  if (!name) return '?';
  
  // Extract first character of each word, max 2 characters
  const initials = name
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join('');
    
  return initials || name.charAt(0).toUpperCase();
};

/**
 * Gets the display name for a user, with fallbacks
 * @param user User details object
 * @returns Best available display name
 */
export const getDisplayName = (user: { display_name?: string; email?: string } | null | undefined): string => {
  if (!user) return 'Unknown User';
  
  return (
    user.display_name || 
    (user.email ? user.email.split('@')[0] : null) ||
    user.email ||
    'Unknown User'
  );
};

/**
 * Interface for assignee information to be added to tasks
 */
export interface AssigneeInfo {
  assignee_name?: string;
  assignee_initials?: string;
}

/**
 * Enriches a task with assignee information based on assigned_to field
 * @param task Task item that might have assigned_to field
 * @returns The same task with additional assignee_name and assignee_initials fields
 */
export function enrichTaskWithAssigneeInfo<T extends { assigned_to?: string | null } & Record<string, any>>(
  task: T
): T & AssigneeInfo {
  // If task is not assigned, return as is
  if (!task.assigned_to) {
    return task as T & AssigneeInfo;
  }

  try {
    // Check if user info is in the cache
    const cachedUser = getCachedUser(task.assigned_to);
    
    if (cachedUser) {
      const displayName = getDisplayName(cachedUser);
      const initials = getInitials(displayName);
      
      return {
        ...task,
        assignee_name: displayName,
        assignee_initials: initials
      };
    }
    
    // If user not in cache, return task without enrichment
    return task as T & AssigneeInfo;
  } catch (error) {
    logger.warn('Failed to enrich task with assignee info', { 
      taskId: task.id, 
      assignedTo: task.assigned_to,
      error: error instanceof Error ? error.message : String(error)
    });
    
    // Return task as is if any error occurs
    return task as T & AssigneeInfo;
  }
}

/**
 * Enriches an array of tasks with assignee information
 * @param tasks Array of task items
 * @returns Same array with tasks enriched with assignee information
 */
export function enrichTasksWithAssigneeInfo<T extends { assigned_to?: string | null } & Record<string, any>>(
  tasks: T[]
): (T & AssigneeInfo)[] {
  return tasks.map(task => enrichTaskWithAssigneeInfo(task));
}